import { createFileRoute, notFound } from '@tanstack/react-router';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import { absoluteUrl, startUrl } from '../lib/site.js';
import { track } from '../lib/analytics.js';
import {
  fetchPost,
  fetchPosts,
  formatPostDate,
  moreArticles,
  type Post,
} from '../content/posts.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Heading, Mark, Text } from '../components/ui/type.js';
import { ButtonAnchor, SiteLink } from '../components/ui/button.js';
import { Breadcrumbs } from '../components/site/Breadcrumbs.js';
import { RichText } from '../components/blog/RichText.js';

/**
 * One article.
 *
 * ===========================================================================
 * The URLs this route is responsible for
 * ===========================================================================
 * Twelve `/blog/<slug>` paths are indexed today and served by the outgoing Next
 * application — `INDEXED_BLOG_SLUGS` in `lib/legacy-urls.ts` lists them. They
 * keep their URLs exactly: the content still comes from the same Payload
 * collection, so nothing is republished, re-slugged, or redirected.
 *
 * ===========================================================================
 * A missing post is a 404, not an empty article
 * ===========================================================================
 * `fetchPost` returns null both for "no such slug" and for "the CMS did not
 * answer", and both become `notFound()`. Rendering a bare page at an indexed
 * URL would tell a crawler the article is gone; a 404 is the honest answer and
 * resolves itself the moment the CMS is back.
 */
export const Route = createFileRoute('/blog/$slug')({
  loader: async ({ params }) => {
    /*
      Both in one round trip. `moreArticles` reads the same list the index
      renders, so the footer of an article costs nothing beyond a request that
      would otherwise be made twice.
    */
    const [post, list] = await Promise.all([
      fetchPost(params.slug),
      fetchPosts(),
    ]);

    if (!post) throw notFound();
    return { post, more: moreArticles(list.posts, post.slug) };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return {};

    const path = `/blog/${post.slug}`;
    const { meta, links } = seo({
      title: post.title,
      description: post.excerpt,
      path,
      image: post.cover?.url,
      type: 'article',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Blog', path: '/blog' },
              { name: post.title, path },
            ])
          ),
        },
        {
          /*
            Article structured data, built from the same fields the page draws.
            `jsonLd` escapes `<`, so a title containing markup cannot break out
            of the script tag.
          */
          type: 'application/ld+json',
          children: jsonLd({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.excerpt,
            url: absoluteUrl(path),
            ...(post.cover ? { image: post.cover.url } : {}),
            ...(post.date ? { datePublished: post.date } : {}),
            ...(post.author ? { author: { '@type': 'Person', name: post.author.name } } : {}),
            publisher: { '@type': 'Organization', name: 'ReviewsJet' },
          }),
        },
      ],
    };
  },
  component: Article,
});

function Article() {
  const { post, more } = Route.useLoaderData();
  const date = formatPostDate(post.date);

  return (
    <>
      <Section size="tight" className="pb-0">
        <Breadcrumbs
          trail={[
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` },
          ]}
        />

        <Measure className="mt-8">
          {/*
            The post's title is the page's only `<h1>`. Headings inside the body
            are shifted down a level by `RichText` so an editor writing an `h1`
            cannot give the page a second one.
          */}
          <Heading level={1} size="hero" className="mt-4 lg:text-display-lg">
            {post.title}
          </Heading>

          {post.excerpt ? (
            <Text size="lead" className="mt-5">
              {post.excerpt}
            </Text>
          ) : null}

          <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-body-sm text-ink-500">
            {post.author?.avatar ? (
              <img
                src={post.author.avatar.url}
                alt=""
                width={36}
                height={36}
                loading="lazy"
                className="size-9 rounded-full object-cover"
              />
            ) : null}
            {post.author ? (
              <span className="font-medium text-ink-800">{post.author.name}</span>
            ) : null}
            {post.author && date ? <span aria-hidden>·</span> : null}
            {date ? <time dateTime={post.date ?? undefined}>{date}</time> : null}
          </div>
        </Measure>
      </Section>

      {post.cover ? (
        <Section size="tight" className="pt-8 pb-0">
          <img
            src={post.cover.url}
            alt={post.cover.alt}
            width={post.cover.width}
            height={post.cover.height}
            loading="eager"
            fetchPriority="high"
            className="h-auto w-full rounded-2xl object-cover ring-1 ring-ink-150"
          />
        </Section>
      ) : null}

      <Section aria-label="Article">
        <RichText content={post.content} />

        {post.tags.length > 0 ? (
          <ul className="mt-12 flex max-w-prose flex-wrap gap-2">
            {post.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-md bg-ink-50 px-2.5 py-1 text-micro font-medium text-ink-600 ring-1 ring-ink-150"
              >
                {tag}
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      {/* ------------------------------------------------------------- CTA */}
      <Section tone="tinted" size="tight" aria-labelledby="cta-heading">
        <Measure>
          <Heading level={2} id="cta-heading" size="display">
            Reading about it is the <span className="whitespace-nowrap"><Mark>slow way</Mark>.</span>
          </Heading>
          <Text className="mt-3 max-w-prose">
            Import the reviews your customers already left, choose the ones worth
            showing, and put them on your site. The free plan does not ask for a
            card.
          </Text>
          <div className="mt-7">
            <ButtonAnchor
              href={startUrl(`blog-${post.slug}`)}
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: `blog-${post.slug}` })
              }
            >
              Start free
            </ButtonAnchor>
          </div>
        </Measure>
      </Section>

      {more.length > 0 ? (
        <Section aria-labelledby="more-heading">
          <Heading level={2} id="more-heading" size="display">
            Read next
          </Heading>
          <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {more.map((next) => (
              <li key={next.slug}>
                <NextCard post={next} />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </>
  );
}

function NextCard({ post }: { post: Post }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl bg-white ring-1 ring-ink-150 transition-shadow duration-[var(--duration-base)] hover:shadow-md">
      {post.cover ? (
        <img
          src={post.cover.url}
          alt=""
          width={post.cover.width}
          height={post.cover.height}
          loading="lazy"
          decoding="async"
          className="aspect-[16/10] w-full object-cover"
        />
      ) : (
        <div className="aspect-[16/10] w-full bg-ink-25" aria-hidden />
      )}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-title text-ink-950">
          <SiteLink
            to={`/blog/${post.slug}`}
            className="rounded-sm underline-offset-4 group-hover:underline"
          >
            {post.title}
          </SiteLink>
        </h3>
        {post.excerpt ? (
          <p className="mt-2.5 line-clamp-3 text-body-sm text-ink-600">
            {post.excerpt}
          </p>
        ) : null}
      </div>
    </article>
  );
}
